// Sales report pro ownera a manažera (mockup v8, obrazovka 11).
// 15. 10. 2026 (Láďa, schválený mockup „Sales report v2“): report ukazuje práci sales
// v období. Hovory a čas z docházky, výsledek hovorů s důvody, zkušební domluvené
// sales v tomto období (dlaždice jako filtr a tabulka, ZkusebniReportSeznam). Zkušební převzaté z kalendáře, domluvené někým
// jiným nebo z jiného období sem nepatří. Výběr období: tmavý pruh se šipkami
// (varianta A), pod ním druh období. Otevírá se na tomhle týdnu.
import React, { useCallback, useEffect, useState } from "react";
import { BarChart3, Send, Phone, ListChecks, CalendarCheck, ChevronLeft, ChevronRight } from "lucide-react";
import GradientHeader from "../../components/GradientHeader.jsx";
import { Stranka, Dlazdice } from "../../components/ui.jsx";
import { toast } from "../../components/Toast.jsx";
import { salesGet, salesPost, dm, addDays, dnesIso } from "../../components/sales/salesApi.js";
import { Pil, Oznameni, KartaLista } from "../../components/sales/salesUi.jsx";
import ZkusebniReportSeznam from "../../components/sales/ZkusebniReportSeznam.jsx";

const MESICE = ["leden", "únor", "březen", "duben", "květen", "červen", "červenec", "srpen", "září", "říjen", "listopad", "prosinec"];
const TYPY = [{ key: "tyden", label: "Týden" }, { key: "mesic", label: "Měsíc" }, { key: "rok", label: "Rok" }];

function posun(typ, od, smer) {
  if (typ === "rok") return `${Number(od.substring(0, 4)) + smer}-06-01`;
  if (typ === "mesic") { const [y, m] = od.split("-").map(Number); const d = new Date(Date.UTC(y, m - 1 + smer, 1)); return d.toISOString().substring(0, 10); }
  return addDays(od, 7 * smer);
}
function rozsah(o) {
  if (!o) return "…";
  if (o.typ === "rok") return `Rok ${o.od.substring(0, 4)}`;
  if (o.typ === "mesic") { const [y, m] = o.od.split("-").map(Number); return `${MESICE[m - 1].replace(/^./, (c) => c.toUpperCase())} ${y}`; }
  return `${dm(o.od)} až ${dm(o.do)} ${o.do.substring(0, 4)}`;
}
// Podtitulek pod rozsahem: tento týden, minulý týden, před 3 týdny, tento měsíc, letos…
function relativne(o) {
  if (!o) return "";
  const dnes = dnesIso();
  if (o.typ === "rok") { const r = Number(o.od.substring(0, 4)) - Number(dnes.substring(0, 4)); return r === 0 ? "letos" : r === -1 ? "loni" : r < 0 ? `před ${-r} lety` : "příští rok"; }
  if (o.typ === "mesic") {
    const k = (s) => Number(s.substring(0, 4)) * 12 + Number(s.substring(5, 7));
    const r = k(o.od) - k(dnes);
    return r === 0 ? "tento měsíc" : r === -1 ? "minulý měsíc" : r < 0 ? `před ${-r} měsíci` : "příští měsíc";
  }
  const r = Math.round((Date.parse(`${o.od}T12:00:00Z`) - Date.parse(`${addDays(dnes, -((new Date(`${dnes}T12:00:00Z`).getUTCDay() + 6) % 7))}T12:00:00Z`)) / (7 * 86400000));
  return r === 0 ? "tento týden" : r === -1 ? "minulý týden" : r < 0 ? `před ${-r} týdny` : "příští týden";
}
const kc = (n) => `${Math.round(Number(n) || 0).toLocaleString("cs-CZ")} Kč`;
const jedenRadek = (v) => <span className="whitespace-nowrap">{v}</span>;
const hodinyText = (h) => `${String(Math.round((Number(h) || 0) * 10) / 10).replace(".", ",")} h`;
const sklon = (n, a, b, c) => (n === 1 ? a : n >= 2 && n <= 4 ? b : c);

function VyberObdobi({ typ, setTyp, obdobi, onZpet, onVpred, vpredVypnuto }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 rounded-2xl bg-brand-900 p-2 text-white">
        <button type="button" onClick={onZpet} aria-label="Předchozí období" title="Předchozí období"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <div className="min-w-0 flex-1 text-center">
          <div className="truncate text-base font-bold sm:text-lg">{rozsah(obdobi)}</div>
          <div className="text-xs text-white/70 sm:text-sm">{relativne(obdobi)}</div>
        </div>
        <button type="button" onClick={onVpred} disabled={vpredVypnuto} aria-label="Následující období" title="Následující období"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20 disabled:opacity-30">
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
      <div className="flex flex-wrap gap-2" role="group" aria-label="Druh období">
        {TYPY.map((t) => (
          <button key={t.key} type="button" onClick={() => setTyp(t.key)} aria-pressed={typ === t.key}
            className={`min-h-[40px] rounded-full border px-4 text-sm font-semibold transition-colors ${typ === t.key ? "border-brand-300 bg-brand-100 text-brand-deep" : "border-brand-200 bg-white text-slate-600 hover:border-brand-300"}`}>
            {t.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function RadekVysledku({ popis, n, max, barva }) {
  return (
    <div className="flex items-center gap-3 py-1.5">
      <span className="w-28 shrink-0 text-sm font-semibold text-slate-700">{popis}</span>
      <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-brand-100"><div className={`h-full rounded-full ${barva}`} style={{ width: `${max > 0 ? Math.max(n > 0 ? 3 : 0, (n / max) * 100) : 0}%` }} /></div>
      <span className="w-8 shrink-0 text-right text-sm font-bold tabular-nums text-slate-900">{n}</span>
    </div>
  );
}

export default function SalesReport() {
  const [typ, setTyp] = useState("tyden");
  const [od, setOd] = useState(() => dnesIso());
  const [r, setR] = useState(null);
  const [chyba, setChyba] = useState("");
  const [posilam, setPosilam] = useState(false);

  const nacti = useCallback(async () => {
    setR(null); setChyba("");
    try { setR(await salesGet("report", { obdobi: typ, od })); } catch (e) { setChyba(e.message); }
  }, [typ, od]);
  useEffect(() => { nacti(); }, [nacti]);

  const doSlacku = async () => {
    setPosilam(true);
    try { await salesPost("report_slack", { od: r?.obdobi?.typ === "tyden" && r.obdobi.do < dnesIso() ? r.obdobi.od : addDays(dnesIso(), -7) }); toast("Týdenní souhrn odeslán do Slacku.", "success"); }
    catch (e) { toast(e.message, "error"); }
    finally { setPosilam(false); }
  };

  const c = r?.cisla;
  const v = c?.vysledek;
  const maxV = v ? Math.max(1, v.zvedla, v.nezvedla, c.zkusebniDomluveno, v.nezajem, v.nq) : 1;
  const duvody = v ? [...v.duvodyNezajmu.map((d) => ({ ...d, nq: false })), ...v.duvodyNq.map((d) => ({ ...d, nq: true }))] : [];
  return (
    <Stranka>
      <GradientHeader title="Sales report" icon={BarChart3}
        akce={[{ klic: "slack", ikona: Send, popis: "Poslat týdenní souhrn do Slacku", onClick: doSlacku, disabled: posilam || !r }]}
        napoveda="Práce sales za týden, měsíc nebo rok: hovory, odpracovaný čas, výsledek hovorů a zkušební, které sales v tom období domluvila. Souhrn chodí každé pondělí v 8:00 do Slacku." />
      <VyberObdobi typ={typ} setTyp={(t) => { setTyp(t); setOd(dnesIso()); }} obdobi={r?.obdobi}
        onZpet={() => setOd(posun(typ, r?.obdobi?.od || od, -1))} onVpred={() => setOd(posun(typ, r?.obdobi?.od || od, 1))}
        vpredVypnuto={!!r && r.obdobi.do >= dnesIso()} />
      {chyba && <Oznameni ton="problem">{chyba}</Oznameni>}
      {!r ? <div className="space-y-3">{[0, 1, 2].map((i) => <div key={i} className="h-24 animate-pulse rounded-2xl bg-brand-light" />)}</div> : (
        <>
          {r.upozorneni.length > 0 && <div className="space-y-2">{r.upozorneni.map((u, i) => <Oznameni key={i} ton={u.ton}><b>{u.text}</b></Oznameni>)}</div>}
          <KartaLista ikona={Phone} nazev="Hovory a čas">
            <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-5">
              <Dlazdice popisek="Hovorů" hodnota={jedenRadek(c.hovory)} ton="info" doplnky={<span className="text-xs font-semibold text-slate-500">na {c.kontaktuVolano} {sklon(c.kontaktuVolano, "kontakt", "kontakty", "kontaktů")}</span>} />
              <Dlazdice popisek="Odpracováno" hodnota={jedenRadek(hodinyText(c.hodiny))} ton="znacka" doplnky={<span className="text-xs font-semibold text-slate-500">z docházky</span>} />
              <Dlazdice popisek="Čas na zvednutý" hodnota={jedenRadek(c.minutNaZvednuty == null ? "?" : `${c.minutNaZvednuty} min`)} ton="pozor" doplnky={<span className="text-xs font-semibold text-slate-500">{v.zvedla} {sklon(v.zvedla, "zvedla", "zvedly", "zvedlo")}</span>} />
              <Dlazdice popisek="Zkušební domluveno" hodnota={jedenRadek(c.zkusebniDomluveno)} ton="ok" doplnky={<span className="text-xs font-semibold text-slate-500">z hovorů v období</span>} />
              <Dlazdice popisek="Přinesly" hodnota={jedenRadek(kc(c.prinesly))} ton="ok" doplnky={<span className="text-xs font-semibold text-slate-500">zaplacené faktury</span>} />
            </div>
          </KartaLista>
          <KartaLista ikona={ListChecks} nazev="Výsledek hovorů">
            <RadekVysledku popis="Zvedla" n={v.zvedla} max={maxV} barva="bg-brand" />
            <RadekVysledku popis="Nezvedla" n={v.nezvedla} max={maxV} barva="bg-brand-300" />
            <RadekVysledku popis="Zkušební" n={c.zkusebniDomluveno} max={maxV} barva="bg-emerald-500" />
            <RadekVysledku popis="Nemá zájem" n={v.nezajem} max={maxV} barva="bg-amber-500" />
            <RadekVysledku popis="NQ lead" n={v.nq} max={maxV} barva="bg-rose-500" />
            {duvody.length > 0 && (
              <div className="mt-3 border-t border-slate-100 pt-3">
                <div className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-500">Proč</div>
                <div className="flex flex-wrap gap-2">{duvody.map((d) => <Pil key={`${d.nq ? "nq" : "nz"}-${d.duvod || "x"}`} ton={d.nq ? "problem" : "pozor"} casti={[d.nq ? `NQ: ${d.text}` : d.text, String(d.n)]} />)}</div>
              </div>
            )}
          </KartaLista>
          <KartaLista ikona={CalendarCheck} nazev="Zkušební domluvené v tomto období" akce={<span className="shrink-0 rounded-full border border-white/25 bg-white/10 px-2.5 py-1 text-sm font-bold tabular-nums text-white">{(c.zkusebniSeznam || []).length}</span>}>
            <ZkusebniReportSeznam seznam={c.zkusebniSeznam} />
          </KartaLista>
        </>
      )}
    </Stranka>
  );
}
